import ky from "ky"
import { destroyCookie, parseCookies } from "nookies"
import { verifyJwt } from "@lib/functions/jwt"
import { JWT_SECRET, TOKEN_COOKIES } from "@const/shared"
import {
  ErrorResponse,
  RawResponse,
  ValidatedResponse,
  ValidationError,
} from "@interfaces/response"

export default class KyMethod {
  private readonly errorMessage: string
  private readonly instance: typeof ky
  private readonly apiVersion: "v2" | "v3"

  constructor(baseUrl?: string, apiVersion: "v2" | "v3" = "v2") {
    this.errorMessage = "Terjadi kesalahan. Silahkan coba lagi"
    this.apiVersion = apiVersion
    this.instance = ky.extend({
      prefixUrl:
        baseUrl ||
        process.env.API_ENDPOINT ||
        process.env.HARISENIN_API_ENDPOINT,
    })
  }

  _tokenChecker() {
    const parsedCookies = parseCookies()
    const token = parsedCookies.HSTOKENID

    if (!token) {
      return null
    }

    if (!verifyJwt(token, JWT_SECRET)) {
      destroyCookie(null, TOKEN_COOKIES)
    }

    return token
  }

  protected async _getResponse<T>(res: Response): Promise<ValidatedResponse<T>> {
    if (!res.ok) {
      return {
        error: "Terjadi kesalahan. Silahkan coba lagi",
        error_code: "SERVER_ERROR",
      }
    }

    const response = (await res.json()) as RawResponse<T>

    if (response.error || response.error_code) {
      return {
        error: response.message ?? "Terjadi kesalahan. Silahkan coba lagi",
        error_code: response.error_code ?? "SERVER_ERROR",
        result: response.result,
        error_data: response.error,
      }
    }

    return {
      error: "",
      error_code: "",
      result: response.result ?? (response as unknown as T),
    }
  }


  protected async _getRawResult<T>(res: Response): Promise<T> {
    const data = await res.json() as { result: T }

    return data.result as T
  }

  protected async _getErrorObject(error: any): Promise<ErrorResponse> {
    const errResponse = error.response

    if (!errResponse) {
      return {
        message: "Terjadi kesalahan",
        error_code: "SERVER_ERROR",
      }
    }

    const errorData = await errResponse.json()

    if (errorData.error_code) {
      return {
        message: errorData.message,
        error_code: errorData.error_code,
      }
    } else {
      const errors = errorData.errors as ValidationError

      return {
        message: errors.message,
        error_code: errors.validation,
      }
    }
  }

  /**
   * Get method
   */
  protected async _getData(url: string, query: any = {}, version?: "v2" | "v3") {
    return this.instance.get((version ?? this.apiVersion) + url, {
      searchParams: query,
    })
  }

  /**
   * CRUD method for API
   */
  protected async _crudMethod(
    url: string,
    body: any = {},
    options?: {
      method: string
      bodyType?: "form-data" | "json"
    }
  ) {
    const type = options?.bodyType ?? "json"

    return this.instance(this.apiVersion + url, {
      method: options?.method ?? "post",
      ...(type === "form-data" && { body }),
      ...(type === "json" && { json: body }),
    })
  }

  /**
   * GET method for protected API
   */
  protected async _getProtectedData(
    url: string,
    options?: {
      version?: "v2" | "v3"
      query?: any
    }
  ) {
    const token = this._tokenChecker()

    if (token) {
      return this.instance.get((options?.version ?? this.apiVersion) + url, {
        ...(options?.query && { searchParams: options.query }),
        headers: {
          Authorization: "Bearer " + token,
        },
      })
    } else {
      throw {
        statusCode: 401,
        message: "Sesi login telah habis. Silahkan login ulang",
      }
    }
  }

  /**
   * CRUD method for protected API
   */
  protected async _protectedCrudMethod(
    url: string,
    options?: {
      body?: any
      version?: "v2" | "v3"
      query?: any
      method?: string
      bodyType?: "form-data" | "json"
    }
  ) {
    const type = options?.bodyType ?? "json"
    const token = this._tokenChecker()

    if (token) {
      return this.instance((options?.version ?? this.apiVersion) + url, {
        ...(options?.query && { searchParams: options.query }),
        headers: {
          Authorization: "Bearer " + token,
        },
        ...(type === "form-data" && { body: options?.body }),
        ...(type === "json" && { json: options?.body ?? {} }),
        method: options?.method ?? "post",
      })
    } else {
      throw {
        statusCode: 401,
        message: "Sesi login telah habis. Silahkan login ulang",
      }
    }
  }
}
